import React, { memo, useEffect } from 'react';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { Box, FormHelperText, TextareaAutosize, FormControl, InputLabel, Select, MenuItem, FormLabel } from '@mui/material'
import CustomDatePicker from '../../../../components/CustomDatePicker/CustomDatePicker';
import { useFormikContext } from 'formik';
import DynamicDropDownlist from '../../../AddNewVehicles/components/DynamicDropDownlist';
import { searchBooking, searchClient } from '../../../../actions/formAction';
import PropTypes from 'prop-types';
import AdvancedSearchBookingModal from './AdvancedSearchBookingModal';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/styles';
import FileUpload from '../../../../components/FileUpload';
import ShipmentClientsInfo from './ShipmentClientsInfo';


const gridSx = {
  margin: { xs: '24px 0', md: '64px 49px' },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
  gap: { xs: 2, md: 4 },
 
};


function ShipmentInfo({ disabled }) {
  const { t } = useTranslation();
  const formik = useFormikContext();




  const [searchParams, setSearchParams] = useSearchParams();

  const shipmentId = searchParams.get('id') || null;
  const theme = useTheme();

  const handleOnChangeBooking = (value) => {
    const prev = formik.values.booking;
    // If booking actually changed (different id or one is null) then clear shipment items in all containers
    const changed = (prev?.id || null) !== (value?.id || null);
    if (changed) {
      const clearedContainers = (formik.values.containers || []).map((c) => ({
        ...c,
        shipmentItems: [],
        netWeight: 0,
      }));
      formik.setValues(
        {
          ...formik.values,
          booking: value || null,
          containers: clearedContainers,
        },
        false
      );
    } else {
      formik.setFieldValue('booking', value || null, false);
    }
  };

  // Sync shipmentItems with primaryDescription and primaryBillOfLadingFile changes
  useEffect(() => {
    const containers = formik.values.containers || [];
    let hasUpdates = false;

    const updatedContainers = containers.map((container) => {
      const shipmentItems = container.shipmentItems || [];
      let containerChanged = false;

      const updatedItems = shipmentItems.map((item) => {
        const nextDescription = item.description ?? formik.values.primaryDescription ?? 'Used Vehicles';
        const nextBillOfLadingFile = item.billOfLadingFile || [];

        if (
          item.description !== nextDescription ||
          item.billOfLadingFile !== nextBillOfLadingFile
        ) {
          containerChanged = true;
          hasUpdates = true;
          return {
            ...item,
            description: nextDescription,
            billOfLadingFile: nextBillOfLadingFile,
          };
        }
        return item;
      });

      return containerChanged ? { ...container, shipmentItems: updatedItems } : container;
    });

    if (hasUpdates) {
      formik.setFieldValue('containers', updatedContainers, false);
    }
  }, [formik.values.primaryDescription,  formik.values.containers, formik]);

  return (
    <>
      <PageHeader title={t('Shipment Information')} />

      <Box sx={{margin: { xs: '24px 0', md: '64px 49px' },display: 'grid',gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
   
}}>

       <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 1' } }}>
          <FormControl fullWidth variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel id="shipment-mode-label">{t('Shipment Mode')}</InputLabel>
            <Select
              labelId="shipment-mode-label"
              id="shipmentMode"
              name="shipmentMode"
              label={t('Shipment Mode')}
              value={formik.values.shipmentMode || ''}
              onChange={(e) => {
                const value = e.target.value;
                formik.setFieldValue('shipmentMode', value, false);
                const params = new URLSearchParams(searchParams);
                params.set('shipmentMode', value);
                formik.setFieldValue('consignee', null, false);
                formik.setFieldValue('notifyParty', null, false);
                formik.setFieldValue('consigneeDock', null, false);
                formik.setFieldValue('shipmentClients', [], false);

                const cleared = (formik.values.containers || []).map((c) => ({
                  ...c,
                  shipmentItems: [],
                  netWeight: 0,
                }));
                formik.setFieldValue('containers', cleared, false);
                formik.setFieldValue('clientExporter', null, false);
                setSearchParams(params, { replace: true });
              }}
              onBlur={formik.handleBlur}
              disabled={disabled}
            >
              <MenuItem value="FCL">{t('Full Container Load')} (FCL)</MenuItem>
              <MenuItem value="LCL">{t('Less than Container Load')} (LCL)</MenuItem>
              <MenuItem value="SPL">{t('Split container loaded')} (SPL)</MenuItem>
            </Select>
            {formik.touched.shipmentMode && formik.errors.shipmentMode && (
              <FormHelperText error>{formik.errors.shipmentMode}</FormHelperText>
            )}
          </FormControl>
        </Box>
        </Box>

      <Box sx={gridSx}>
        {/* Booking: full width on xs, one column on md+ */}
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 1' } }}>
          <DynamicDropDownlist
            label="Booking Number"
            formik={formik}
            fieldName="booking"
            disabled={disabled}
            loadingKey="isLoadingBookingData"
            dataKey="booking"
            fetchAction={searchBooking}
            errorKey="booking"
            onChange={handleOnChangeBooking}
            dataName="number"
            modalDialog={<AdvancedSearchBookingModal bookingId={shipmentId === null ? null : formik.values.booking?.id} />}
          />
        </Box>

        {/* Client: full width on xs, one column on md+ */}
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 1' } }}>
          <DynamicDropDownlist
            label="Primary Client Name"
            dataName2="company"
            formik={formik}
            fieldName="primaryClient"
            disabled={disabled}
            loadingKey="isLoadingClientData"
            dataKey="client"
            fetchAction={searchClient}
            errorKey="primaryClient"
            onChange={(val) => {
              const prevId = formik.values.primaryClient?.id || null;
              const nextId = val?.id || null;
              const changed = prevId !== nextId;
              formik.setFieldValue('primaryClient', val, false);
              if (changed) {
                // Always clear dependent parties & vehicles when client changes
                formik.setFieldValue('clientExporter', val?.exporter || null, false);
                formik.setFieldValue('exporter', val?.exporter || null, false);
                formik.setFieldValue('consignee', null, false);
                formik.setFieldValue('notifyParty', null, false);
                formik.setFieldValue('consigneeDock', null, false);
                // Remove shipmentItems that contain vehicles with the old client.id
                const cleared = (formik.values.containers || []).map((c) => {
                  const filteredItems = (c.shipmentItems || []).filter(
                    (item) => item.vehicle?.client?.id !== prevId
                  );
                  // Recalculate net weight after removing items
                  const newNetWeight = filteredItems.reduce((sum, item) => sum + (item.netWeight || 0), 0);
                  return {
                    ...c,
                    shipmentItems: filteredItems,
                    netWeight: newNetWeight,
                  };
                });
                formik.setFieldValue('containers', cleared, false);
              }
            }}
             extraQueryParams={{ withExporter: true }}

          />
        </Box>

       

        <FileUpload
                  disabled={disabled}
                  label={t("Bill of Lading File")}
                  formikName="primaryBillOfLadingFile"
                  uploadName="BillOfLadingFile"
                  accept=".pdf"
          />

        {/* Entry Date: full width on xs, one column on md+ (third column) */}
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 1' } }}>
          <CustomDatePicker disabled={true} label={t('entryDate')} name="entryDate" disableFuture={true} />
        </Box>


        <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>


      {formik.values.shipmentMode === 'LCL' && (
                      <ShipmentClientsInfo disabled={disabled} />


                    )}

      </Box>

        {/* Description: always span full width */}
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
          <FormLabel sx={{ mb: 1, color: theme.palette.text.primary }}>{t('Primary Description')}</FormLabel>
          <TextareaAutosize
            style={{
              minHeight: '90px',
              border: `1px solid ${theme.palette.grey[300]}`,
              
              borderRadius: '8px',
              padding: '10px',
              resize: 'none',
              fontFamily: 'inherit',
              fontSize: 15,
              color: theme.palette.text.primary,
            }}
            disabled={disabled}
            placeholder={t('description')}
            name="primaryDescription"
            value={formik.values.primaryDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.primaryDescription && Boolean(formik.errors.primaryDescription)}
          />
          {formik.touched.primaryDescription && formik.errors.primaryDescription && (
            <FormHelperText error>{formik.errors.primaryDescription}</FormHelperText>
          ) }
        </Box>
      </Box>

        
    </>
  );
}

ShipmentInfo.propTypes = {
  disabled: PropTypes.bool,
};

export default memo(ShipmentInfo);
